import React, { useState } from 'react';
import { Alert, Button, Card, CardBody, CustomInput, InputGroup, InputGroupAddon, Table } from 'reactstrap';
import { toast } from 'react-toastify';
import LoadingOverlay from 'react-loading-overlay-ts';
import { parseRolesCSV } from './rolescsvparser';
import { downloadSampleRolesCsv } from './samplerolestemplate';
import { PERMISSION_COLUMNS_DISPLAY } from './permissioncolumns';
import {
  getImportRolesUploadUrl,
  uploadImportRolesCSV,
  validateImportRoles,
  processImportRoles,
  ImportRolesValidationResult,
  ImportRoleRecord,
} from '../../api/roles';
import { useUserContext } from '../../contexts/UserContext';
import NotAuthorized from '../../users/userprofile/notauthorized';
import styles from '../../styles/archiveui.module.css';

// Cap CSV size client-side because the /validate endpoint POSTs parsed records as
// JSON through API Gateway (10MB request limit). JSON expands the CSV roughly 2x
// due to per-field property names, so 5MB of CSV ≈ ~10MB JSON. ~5MB is around
// 50,000-70,000 role rows, which is far above any realistic SOL → DM batch.
// The same cap is enforced server-side via the presigned S3 POST condition in
// api/src/import-roles/lambda.ts (MAX_UPLOAD_BYTES) — keep both in sync.
const MAX_CSV_BYTES = 5 * 1024 * 1024;
const MAX_CSV_MB_LABEL = '5 MB';

const ImportRoles = (): JSX.Element => {
  const {
    currentAccount,
    profile: { user },
  } = useUserContext();
  const operatorAccountId = currentAccount.accountId;

  // Import Roles is a Scribbles-admin-only feature: it operates across accounts
  // and the API Gateway IAM policy only grants access to admins (no dedicated
  // permission exists for /importroles/* — see api/src/auth/definedpermissions.ts).
  if (!user.isScribblesAdmin) {
    return <NotAuthorized />;
  }

  const [selectedFile, setSelectedFile] = useState<File>();
  const [records, setRecords] = useState<ImportRoleRecord[]>([]);
  const [validation, setValidation] = useState<ImportRolesValidationResult>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState<boolean>(false);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [fileInputKey, setFileInputKey] = useState<number>(0);

  const clearAll = () => {
    setSelectedFile(undefined);
    setRecords([]);
    setValidation(undefined);
    setError(undefined);
    setFileInputKey((prev) => prev + 1);
  };

  const fileChange = (e: React.FormEvent<HTMLInputElement>) => {
    // Reset all derived state so a stale preview can't be submitted with a new file.
    setRecords([]);
    setValidation(undefined);
    setError(undefined);
    if (e.currentTarget.files && e.currentTarget.files.length > 0) {
      const f = e.currentTarget.files.item(0);
      setSelectedFile(f ?? undefined);
    } else {
      setSelectedFile(undefined);
    }
  };

  const parseAndValidate = async () => {
    if (!selectedFile) {
      setError('Please select a file first');
      return;
    }
    if (selectedFile.size > MAX_CSV_BYTES) {
      const msg = `File is too large. Maximum supported size is ${MAX_CSV_MB_LABEL}. Split the import into smaller batches.`;
      setError(msg);
      toast.error(msg);
      return;
    }
    setLoading(true);
    setError(undefined);
    try {
      const fileText = await selectedFile.text();
      const parsed = parseRolesCSV(fileText);
      setRecords(parsed.records);

      const v = await validateImportRoles(operatorAccountId, parsed.headers, parsed.records);
      setValidation(v);

      if (v.validationErrors.length > 0) {
        toast.warn(`${v.validationErrors.length} validation issue(s) found. Review below.`);
      } else if (v.skippedRows.length > 0) {
        toast.info(`${v.validRows} new role(s) will be imported; ${v.skippedRows.length} already exist and will be skipped.`);
      } else {
        toast.success(`All ${v.validRows} role(s) ready to import.`);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to parse CSV';
      setError(msg);
      toast.error(msg);
      setRecords([]);
      setValidation(undefined);
    } finally {
      setLoading(false);
    }
  };

  const submitForProcessing = async () => {
    if (!selectedFile || !validation) return;
    if (validation.validationErrors.length > 0) {
      setError('Fix validation errors before submitting');
      return;
    }
    setIsProcessing(true);
    setError(undefined);
    setUploadProgress(0);
    try {
      toast.info('Preparing upload...');
      const { uploadUrl, fields, s3Key } = await getImportRolesUploadUrl(operatorAccountId);
      toast.info('Uploading CSV...');
      await uploadImportRolesCSV(selectedFile, uploadUrl, fields, setUploadProgress);
      toast.info('Starting processing...');
      const response = await processImportRoles(operatorAccountId, s3Key);
      if (response.success) {
        toast.success(response.message);
        clearAll();
      } else {
        setError('Failed to start processing');
        toast.error('Failed to start processing');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to import roles';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsProcessing(false);
      setUploadProgress(0);
    }
  };

  const hasErrors = !!validation && validation.validationErrors.length > 0;
  const hasSkipped = !!validation && validation.skippedRows.length > 0;
  const canSubmit = !!validation && !hasErrors && validation.validRows > 0;

  return (
    <div>
      <LoadingOverlay
        active={loading || isProcessing}
        spinner
        text={
          isProcessing
            ? uploadProgress > 0 && uploadProgress < 100
              ? `Uploading... ${Math.round(uploadProgress)}%`
              : 'Processing your file...'
            : 'Parsing & validating...'
        }
      >
        <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
          <CardBody className={styles.searchBarHeader}>
            <h5 style={{ textAlign: 'start', fontWeight: 500, marginTop: '.5em' }}>Import Roles</h5>
          </CardBody>
          <CardBody className={styles.searchBarBody}>
            <div className="row">
              <div className="col">
                <InputGroup>
                  <CustomInput key={fileInputKey} id="rolesCsvInput" type="file" onChange={fileChange} accept=".csv" />
                  <InputGroupAddon addonType="append">
                    <Button className={styles.scrbBtnBlue} disabled={!selectedFile} onClick={parseAndValidate}>
                      Parse &amp; Validate
                    </Button>
                    <Button color="danger" onClick={clearAll} disabled={!selectedFile && records.length === 0}>
                      Clear
                    </Button>
                  </InputGroupAddon>
                </InputGroup>
              </div>
            </div>
            {error && (
              <Alert color="danger" style={{ marginTop: '1em', marginBottom: '0px' }}>
                {error}
              </Alert>
            )}
            <div style={{ marginTop: '1em', fontSize: '0.9em', color: '#666' }}>
              <strong>Required CSV format:</strong>
              <div style={{ marginTop: '0.5em', fontFamily: 'monospace', fontSize: '0.85em' }}>
                sitename,id,name,description,accountid,DocumentCreate,DocumentRead,...,AuditLogRead
              </div>
              <div style={{ marginTop: '0.5em' }}>
                Required columns: <strong>id</strong>, <strong>name</strong>, <strong>accountid</strong>. Optional: <em>sitename</em>, <em>description</em>.
                Roles that already exist for an account are skipped, not overwritten.
              </div>
              <div style={{ marginTop: '0.5em' }}>
                Permission columns (all optional):{' '}
                {PERMISSION_COLUMNS_DISPLAY.map((col, i) => (
                  <React.Fragment key={col}>
                    <strong>{col}</strong>
                    {i < PERMISSION_COLUMNS_DISPLAY.length - 1 ? ', ' : '. '}
                  </React.Fragment>
                ))}
                Use <code>A</code> for Allow, <code>D</code> for Deny, or leave blank for Not Set.
              </div>
              <div style={{ marginTop: '0.5em' }}>
                Maximum file size: <strong>{MAX_CSV_MB_LABEL}</strong>. For larger migrations, split into multiple imports.
              </div>
              <div style={{ marginTop: '0.5em' }}>
                <Button color="link" style={{ padding: 0 }} onClick={downloadSampleRolesCsv}>
                  Download sample CSV
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {validation && (
          <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
            <CardBody>
              <Alert color={hasErrors ? 'danger' : 'info'} style={{ marginBottom: '1em' }}>
                <strong>Preview:</strong> Total {validation.totalRows} · To create {validation.validRows}
                {hasSkipped ? ` · Skipped ${validation.skippedRows.length}` : ''}
                {hasErrors ? ` · Errors ${validation.validationErrors.length}` : ''}
              </Alert>

              {hasErrors && (
                <div style={{ marginBottom: '1em' }}>
                  <strong>Validation errors</strong> (first 50 shown):
                  <Table size="sm" striped style={{ marginTop: '0.5em' }}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Field</th>
                        <th>Message</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.validationErrors.slice(0, 50).map((e, i) => (
                        <tr key={i}>
                          <td>{e.rowNumber}</td>
                          <td>{e.field}</td>
                          <td>{e.message}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              {hasSkipped && (
                <div style={{ marginBottom: '1em' }}>
                  <strong>Will be skipped — already exist</strong> (first 50 shown):
                  <Table size="sm" striped style={{ marginTop: '0.5em' }}>
                    <thead>
                      <tr>
                        <th>Row</th>
                        <th>Account</th>
                        <th>Role ID</th>
                        <th>Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {validation.skippedRows.slice(0, 50).map((s, i) => (
                        <tr key={i}>
                          <td>{s.rowNumber}</td>
                          <td>{s.accountid}</td>
                          <td>{s.id}</td>
                          <td>{s.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}

              <Alert color="info" style={{ marginTop: '1em' }}>
                <strong>What happens next:</strong>
                <ul style={{ marginTop: '0.5em', marginBottom: 0 }}>
                  <li>Submitting starts an async import. You will receive an email when it finishes.</li>
                  <li>Roles that already exist are skipped — their permissions and users are preserved.</li>
                  <li>The email contains a per-row results CSV.</li>
                </ul>
              </Alert>

              <div style={{ marginTop: '1em', display: 'flex', gap: '0.5em', justifyContent: 'flex-end' }}>
                <Button className={styles.scrbBtnBlue} disabled={!canSubmit || isProcessing} onClick={submitForProcessing}>
                  {isProcessing ? 'Submitting...' : `Import ${validation.validRows} Role(s)`}
                </Button>
              </div>
            </CardBody>
          </Card>
        )}
      </LoadingOverlay>
    </div>
  );
};

export default ImportRoles;
